import React, { type ReactNode } from 'react';
import {
  NODE_CARD_STATE_DEFAULT,
  NODE_CARD_VARIANT_NEUTRAL,
  type NodeCardEditMode,
  type NodeCardEmphasis,
  type NodeCardState,
  type NodeCardVariant,
} from './NodeCardShell.constants';
import {
  Body,
  Branches,
  CollapseChevron,
  CollapsedHeader,
  CornerRunSlot,
  FloatingActions,
  Header,
  HeaderActions,
  HeaderEnd,
  HeaderIconTile,
  HeaderSubtitle,
  HeaderTitle,
  HeaderTitleStack,
  PulseLayer,
  Root,
} from './NodeCardShell.styled';

export interface NodeCardShellProps {
  /** Visual tint for the header icon-chip and the per-variant accent triplet. */
  variant?: NodeCardVariant;
  /**
   * Composable runtime state — selected / running / completed / failed /
   * invalid / orphan / runDone / default. Drives the outline + halo only;
   * orthogonal flags (`dropTarget`, `editMode`, `published`, `hovered`)
   * layer on top via separate props.
   */
  state?: NodeCardState;
  /** Title shown in the header (e.g. "input", "task · freecad"). */
  title: ReactNode;
  /**
   * Optional secondary line under the title — typically a short context
   * crumb (e.g. agent name, port count). Hidden when omitted; has no
   * impact on header height other than the line it adds.
   */
  subtitle?: ReactNode;
  /** Glyph rendered inside the header's circular icon chip. */
  icon: ReactNode;
  /**
   * Always-visible cluster at the right end of the header (e.g. status
   * pill, persistent close button). Coexists with `actions` — the
   * always-visible `headerEnd` renders to the right of the hover-only
   * `actions` cluster.
   */
  headerEnd?: ReactNode;
  /**
   * Hover-/select-revealed actions cluster (e.g. 3-dot menu). Renders
   * inside the header but with its own opacity transition so the
   * always-visible affordances don't blink while the menu fades in.
   */
  actions?: ReactNode;
  /**
   * Naked-icon cluster floating above the card's top-right corner.
   * Visible only while the card is `selected` (not on hover) — used for
   * info / delete affordances that should not steal attention until the
   * user has committed to the node.
   */
  floatingActions?: ReactNode;
  /**
   * Slot for the corner-FAB Run button — sits 12px from the bottom-right
   * edge of the card and is always visible. Consumer renders the actual
   * button (so the shell stays presentation-only); the shell only
   * positions it.
   */
  runButton?: ReactNode;
  /** Main body — the form fields per node type. */
  children?: ReactNode;
  /**
   * Optional bottom branches slot used by the Gate node (approved /
   * rejected row). Rendered with a top-divider, no padding — branch
   * labels handle their own padding so they line up with their port
   * dots.
   */
  branches?: ReactNode;
  /**
   * Mouse-down on the header is forwarded back so React Flow's drag
   * handle can treat the header as a drag affordance. The body fields
   * don't drag.
   */
  onHeaderMouseDown?: React.MouseEventHandler<HTMLDivElement>;
  /** Width of the card in pixels — defaults to 280 (matches the redesign). */
  width?: number;
  /**
   * When true, the header padding rebalances to a vertically-centred
   * pill (14px top/bottom) and the chevron rotates -90° to point right.
   * The consumer is responsible for hiding the body when collapsed —
   * the shell only adjusts the header chrome.
   */
  collapsed?: boolean;
  /**
   * When provided, renders the leading collapse chevron in the header.
   * Click toggles the chevron rotation via the `collapsed` prop the
   * consumer round-trips back. The shell does NOT manage the collapsed
   * state itself — that lives in the editor context so multiple nodes
   * can share collapse state.
   */
  onToggleCollapsed?: () => void;
  /**
   * Editor mode overlay. `edit` paints a dashed primary outline + a
   * primary-tinted gradient so authors know which node they're
   * authoring. `use` renders the standard run-mode chrome. Omit (or
   * pass `null`) to leave the card in its plain run-mode look.
   */
  editMode?: NodeCardEditMode | null;
  /**
   * When true (and the card is in `editMode='edit'`), the dashed outline
   * is locked to a solid 2px stroke — published cards never read as
   * drafts.
   */
  published?: boolean;
  /**
   * Drop-target affordance — true while the user is dragging a wire
   * over a node that could accept the connection. Paints the outline
   * in `--node-card-accent` so the affordance picks up the variant's
   * semantic color (green for sources, amber for gates, ...).
   */
  dropTarget?: boolean;
  /**
   * Programmatic hover. Used when an external surface (side-panel
   * label, search result) wants to highlight the card without the
   * mouse actually being over it.
   */
  hovered?: boolean;
  /**
   * `highlighted` paints the hover ring without revealing hover-only
   * chrome; `dimmed` fades the card back until the pointer is over it.
   */
  emphasis?: NodeCardEmphasis | null;
  /** Pulse the border (only for Gate-waiting state in the design). */
  pulse?: boolean;
  className?: string;
  dataTestId?: string;
}

const NodeCardShell: React.FC<NodeCardShellProps> = ({
  variant = NODE_CARD_VARIANT_NEUTRAL,
  state = NODE_CARD_STATE_DEFAULT,
  title,
  subtitle,
  icon,
  headerEnd,
  actions,
  floatingActions,
  runButton,
  children,
  branches,
  onHeaderMouseDown,
  width = 280,
  collapsed = false,
  onToggleCollapsed,
  editMode = null,
  published = false,
  dropTarget = false,
  hovered = false,
  emphasis = null,
  pulse = false,
  className,
  dataTestId = 'node-card',
}) => {
  const HeaderRoot = collapsed ? CollapsedHeader : Header;
  return (
    <Root
      $state={state}
      $variant={variant}
      $width={width}
      $editMode={editMode}
      $published={published}
      $dropTarget={dropTarget}
      $hovered={hovered}
      $emphasis={emphasis}
      className={className}
      data-testid={dataTestId}
      data-variant={variant}
      data-state={state}
      data-edit-mode={editMode ?? undefined}
      data-published={published || undefined}
      data-drop-target={dropTarget || undefined}
      data-hovered={hovered || undefined}
      data-emphasis={emphasis ?? undefined}
      data-collapsed={collapsed || undefined}
    >
      {floatingActions && (
        <FloatingActions
          data-testid={`${dataTestId}__floating-actions`}
          className="nodrag"
        >
          {floatingActions}
        </FloatingActions>
      )}
      <HeaderRoot
        onMouseDown={onHeaderMouseDown}
        data-testid={`${dataTestId}__header`}
        className="node-card__drag-handle"
      >
        {onToggleCollapsed && (
          <CollapseChevron
            type="button"
            $collapsed={collapsed}
            aria-label={collapsed ? 'Expand node' : 'Collapse node'}
            aria-expanded={!collapsed}
            onClick={(e) => {
              e.stopPropagation();
              onToggleCollapsed();
            }}
            onMouseDown={(e) => e.stopPropagation()}
            data-testid={`${dataTestId}__collapse`}
            className="nodrag"
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M7 10l5 5 5-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </CollapseChevron>
        )}
        <HeaderIconTile
          $variant={variant}
          aria-hidden="true"
          data-testid={`${dataTestId}__icon`}
        >
          {icon}
        </HeaderIconTile>
        <HeaderTitleStack>
          <HeaderTitle data-testid={`${dataTestId}__title`}>{title}</HeaderTitle>
          {subtitle && (
            <HeaderSubtitle data-testid={`${dataTestId}__subtitle`}>
              {subtitle}
            </HeaderSubtitle>
          )}
        </HeaderTitleStack>
        {(actions || headerEnd) && (
          // {@code nodrag} is critical here: the parent {@link Header}
          // carries the {@code .node-card__drag-handle} className that
          // React Flow's per-node {@code dragHandle} prop points at, so
          // pointer-down anywhere inside it normally initiates a node
          // drag. The other in-header interactive surfaces
          // ({@link CollapseChevron}, {@link FloatingActions},
          // {@link CornerRunSlot}, plus consumer-supplied chrome like
          // the 3-dot menu) all guard themselves with their own
          // {@code nodrag} -- the {@code headerEnd} slot was the
          // outlier. Without this, React Flow swallowed the pointer-up
          // for the per-node Stop button (and the never-noticed Run
          // affordance) so its {@code onClick} never fired and no
          // cancel request reached the network.
          <HeaderEnd className="nodrag" data-testid={`${dataTestId}__end`}>
            {actions && (
              <HeaderActions data-testid={`${dataTestId}__actions`}>
                {actions}
              </HeaderActions>
            )}
            {headerEnd}
          </HeaderEnd>
        )}
      </HeaderRoot>
      {/* When collapsed, skip the Body wrapper but still render children
          inline. Consumers typically pass conditional content alongside
          React Flow `<Handle>` elements (absolute-positioned, no layout
          impact); wrapping a Handles-only `children` in `<Body>` would
          add the body's bottom padding to a card that has no visible
          body, stretching the pill ~16px taller than the design and
          pushing the title above the visual center. Skipping the
          wrapper keeps the collapsed pill's height = header height
          while still mounting the Handles for React Flow. */}
      {children && !collapsed && (
        <Body data-testid={`${dataTestId}__body`}>{children}</Body>
      )}
      {children && collapsed && children}
      {branches && (
        <Branches data-testid={`${dataTestId}__branches`}>{branches}</Branches>
      )}
      {runButton && (
        <CornerRunSlot
          data-testid={`${dataTestId}__run`}
          className="nodrag"
        >
          {runButton}
        </CornerRunSlot>
      )}
      {pulse && <PulseLayer data-testid={`${dataTestId}__pulse`} />}
    </Root>
  );
};

export default NodeCardShell;
